import { Typography, Box, Stack, alpha } from "@mui/material";
import { ReactNode } from "react";
import { PhoneIcon, Location1Icon, MailIcon } from "@/components/common/icons";
import InfoCard from "@/components/common/infoCard/infoCard";
import { VIOLET, PRIMARY } from "@/config/mui/colorPalette";
import { getAddressString } from "@/utils";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { MedspaInfo } from "@/views/booking/bookingView";

type Props = {
  name: string;
  phone: string;
  email: string;
  address: Omit<MedspaInfo["address"], "__typename">;
  customPrimaryColor?: string;
  medspaLogo?: ReactNode;
  handlePhoneNumberClick?: () => void;
};

export default function MedspaInfoCard({
  name,
  phone,
  email,
  address,
  customPrimaryColor,
  medspaLogo,
  handlePhoneNumberClick,
}: Props) {
  const addressString = getAddressString(address);

  const iconWrapperColor = customPrimaryColor
    ? alpha(customPrimaryColor, 0.15)
    : VIOLET[40];

  const iconColor = customPrimaryColor ? customPrimaryColor : PRIMARY;

  const iconStrokeWidth = customPrimaryColor ? 2 : 1;

  return (
    <InfoCard sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Stack
          sx={{
            alignItems: "center",
            gap: 2,
          }}
        >
          {medspaLogo}
          <Typography
            variant="h3"
            sx={{
              color: "text.primary",
              textAlign: "center",
            }}
          >
            {name}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {phone && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <IconWrapper backgroundColor={iconWrapperColor}>
                <PhoneIcon
                  color={iconColor}
                  size={21}
                  strokeWidth={iconStrokeWidth}
                />
              </IconWrapper>
              <Box>
                <Typography
                  variant="labelTiny"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Phone number
                </Typography>
                <Typography
                  component="a"
                  variant="paragraphSmall"
                  href={`tel:${phone}`}
                  aria-label={`Phone to ${name}`}
                  style={{ wordBreak: "break-word" }}
                  sx={{
                    color: "text.primary",
                  }}
                  onClick={handlePhoneNumberClick}
                >
                  {formatPhoneNumber(phone)}
                </Typography>
              </Box>
            </Box>
          )}
          {email && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <IconWrapper backgroundColor={iconWrapperColor}>
                <MailIcon color={iconColor} strokeWidth={iconStrokeWidth} />
              </IconWrapper>
              <Box>
                <Typography
                  variant="labelTiny"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Email address
                </Typography>
                <Typography
                  component="a"
                  variant="paragraphSmall"
                  href={`mailto:${email}`}
                  aria-label={`Mail to ${name}`}
                  style={{ wordBreak: "break-word" }}
                  sx={{
                    color: "text.primary",
                  }}
                >
                  {email}
                </Typography>
              </Box>
            </Box>
          )}
          {addressString && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <IconWrapper backgroundColor={iconWrapperColor}>
                <Location1Icon
                  color={iconColor}
                  size={22}
                  strokeWidth={iconStrokeWidth}
                />
              </IconWrapper>
              <Box>
                <Typography
                  variant="labelTiny"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Address
                </Typography>
                <Typography variant="paragraphSmall">
                  {addressString}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </InfoCard>
  );
}

function IconWrapper({
  children,
  backgroundColor,
}: {
  children: React.ReactNode;
  backgroundColor: string;
}) {
  return (
    <Box
      sx={{
        backgroundColor,
        borderRadius: "100%",
        width: 32,
        height: 32,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}
